import { formatDateToLocaleString } from '@backrs_ui/common-ui/src/utils/dateTime.util';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { PublicProfileSection } from '@backrs_ui/grow-ui/src/pages/Profile/atoms/PublicProfileSection';
import { type MemberAssessments } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { type CustomTabId } from '@backrs_ui/grow-ui/src/redux/tabs/customTabIds';
import { openCustomTab } from '@backrs_ui/grow-ui/src/redux/tabs/tabs.slice';

import { Box, Button, type SxProps, type Theme, Tooltip, Typography } from '@mui/material';

const {
  modules: { coachingDashboard },
} = appStrings;

type AssessmentStatusSectionProps = Readonly<{
  assessments: MemberAssessments;
}>;

const ASSESSMENT_LABELS: Record<keyof MemberAssessments, string> = {
  blueprint: 'Blueprint',
  life_readiness: 'Life Readiness',
  track_assessment: 'Are You On Track?',
};

const ASSESSMENT_PROMPT_BUTTON_IDS: Record<keyof MemberAssessments, string> = {
  blueprint: 'coach_blueprint_summary',
  life_readiness: 'coach_life_readiness_summary',
  track_assessment: 'coach_track_assessment_summary',
};

const ASSESSMENT_TAB_IDS: Record<keyof MemberAssessments, CustomTabId> = {
  blueprint: 'memberBlueprint',
  life_readiness: 'memberLifeReadiness',
  track_assessment: 'memberAyot',
};

export const AssessmentStatusSection = ({ assessments }: AssessmentStatusSectionProps) => {
  const dispatch = useAppDispatch();
  const entries = Object.entries(ASSESSMENT_LABELS) as [keyof MemberAssessments, string][];

  return (
    <PublicProfileSection
      title="Assessments"
      variant={{
        type: 'CHILDREN',
        children: (
          <Box sx={containerSx}>
            {entries.map(([key]) => {
              const assessment = assessments[key];
              const isComplete = assessment?.completed ?? false;
              const tabId = ASSESSMENT_TAB_IDS[key];

              return (
                <Box key={key} sx={rowSx}>
                  <Tooltip
                    title={coachingDashboard.assessmentNotComplete}
                    placement="top"
                    arrow
                    disableHoverListener={isComplete}
                    disableFocusListener={isComplete}
                  >
                    <Box component="span" sx={buttonWrapSx}>
                      <LubavButton
                        id={`assessment-btn-${key}`}
                        label={ASSESSMENT_LABELS[key]}
                        promptButtonId={ASSESSMENT_PROMPT_BUTTON_IDS[key]}
                        buttonSize="sm"
                        allowTools={false}
                        gray={!isComplete}
                        disabled={!isComplete}
                        sx={buttonSx}
                      />
                    </Box>
                  </Tooltip>
                  {isComplete && assessment?.completed_at ? (
                    <Typography variant="text-xs-regular" sx={metaTextSx}>
                      {formatDateToLocaleString(assessment.completed_at)}
                    </Typography>
                  ) : null}
                  {isComplete ? (
                    <Button
                      id={`assessment-view-${key}`}
                      hierarchy="Tertiary color"
                      size="sm"
                      sx={viewButtonSx}
                      onClick={() => dispatch(openCustomTab(tabId))}
                    >
                      {coachingDashboard.view}
                    </Button>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        ),
      }}
    />
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
});

const buttonWrapSx: SxProps<Theme> = {
  display: 'inline-flex',
};

const viewButtonSx: SxProps<Theme> = {
  padding: 0,
};

const metaTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const buttonSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Base/Black'],
  '&.Mui-disabled': {
    backgroundColor: backrs.colors['Colors/Base/White'],
    borderWidth: '1px',
    borderColor: 'inherit',
    color: 'inherit',
  },
});
